import React, { useEffect, useRef, useState } from 'react';

import extractArrayData from '../../../../utils/extractArrayData';
import { ContentfulBlade, ContentfulForm, FilterCategory, SmartFilterI } from '../../../../ContentfulTypes';
import { Contentful } from '../../../../types';
import FilterDropdown from '../../../Filters/FilterDropdown/FilterDropdown';
import TextLink from '../../../UI/TextLink/TextLink';
import tokenize from '../../../../utils/tokenize';
import FilterDrawer from '../../../Filters/FilterDrawer/FilterDrawer';
import FilterGroup from '../../../Filters/FilterDrawer/FilterGroup/FilterGroup';
import IconPillButton from '../../../IconPillButton/IconPillButton';
import dataLayerFilterSubmit from '../../../../utils/dataLayerFilterSubmit';
import { FiltersContainer, MobileContainer, FilterWrapper } from '../styles';

interface PhoneFiltersProps {
    filterObject: SmartFilterI[];
    onFilterChange: (category: string, item: string) => void;
    resetFilters: () => null;
    storedFilters: Record<string, string>
    bladeVisible: boolean;
    setBladeVisible: (...args: any[]) => any;
    filtersActivated: boolean;
    phonesCount: number;
    filterLabels: ContentfulForm;
    filterBlade: ContentfulBlade;
}

const PhoneFilters = (
  {
    filterObject,
    onFilterChange,
    resetFilters,
    storedFilters,
    bladeVisible,
    setBladeVisible,
    filtersActivated,
    phonesCount,
    filterLabels,
    filterBlade,
  }: PhoneFiltersProps,
) => {
  const [activeDropdown, setActiveDropdown] = useState({});
  const activeDropdownRef = useRef(null);
  const phonesFilterRef = useRef(null);
  const standardFilterData = extractArrayData(filterObject, Contentful.ContentfulId, '5yqnWJtTM5LeQ1VGQMcLEA') as SmartFilterI;

  const phoneFiltersLabel = extractArrayData(
    filterLabels.formFields,
    Contentful.ContentfulId,
    "3kuNNd1J2vYJLgvraK26NH",
  )?.displayLabel;

  const { bladeTitle, bladeCloseLabel } = filterBlade;

  const seePhonesLabel = extractArrayData(
    filterBlade.bladeSections,
    Contentful.ContentfulId,
    "LLsw9POLveDSVLvQpIYfM",
  );

  const phoneNotFoundLabel = extractArrayData(
    filterBlade.bladeSections,
    Contentful.ContentfulId,
    "5K1JDg9PE6oK20ApzGwDbV",
  );

  const clearFiltersLabel = extractArrayData(
    filterBlade.bladeSections,
    Contentful.ContentfulId,
    "4x5Ohgz75YJfIA4W6f0ot",
  );

  // Currently CMS sends filter tag like standard-carrier-bell instead of just bell
  const sanitizeOptionId = (filterTag, category) => filterTag?.cmsName.replace(`${category.cmsName}-`, '');

  const mapSmartFiltersToFilters = (categories: FilterCategory[]) => categories.map((category) => ({
    id: category.cmsName,
    name: category.displayLabel,
    type: "type",
    options: category.filterTags.map((filterTag) => ({
      id: sanitizeOptionId(filterTag, category),
      name: filterTag.displayLabel,
      key: filterTag.cmsName,
      enabled: storedFilters?.[category.cmsName]?.[filterTag.cmsName],
    })),
  }));
  const filters = mapSmartFiltersToFilters(standardFilterData.filterCategories);

  const onClearFilters = () => {
    resetFilters();
    dataLayerFilterSubmit('phonesFilter', 'Clear filters', 'Reset');
  };

  useEffect(() => {
    activeDropdownRef.current = activeDropdown;
  }, [activeDropdown]);

  useEffect(() => {
    const clickHandler = document.documentElement.addEventListener(
      'click',
      () => {
        if (activeDropdownRef.current) setActiveDropdown(null);
      },
      false,
    );
    return () => document.documentElement.removeEventListener(clickHandler, null);
  }, []);
  
  const filteredFilters = filters.filter(({ id }) => id === 'standard-brand' || id === 'standard-carrier' || id === 'standard-offers');

  const scrollToFilters = () => phonesFilterRef.current?.scrollIntoView({ behavior: "smooth" });
  
  return (
    <FiltersContainer>
      {filteredFilters.map((filter) => {
        const activeOptions = filter.options.filter(
          (option) => option.enabled,
        ).length;
        return (
          <FilterDropdown
            key={filter.id}
            classNamePrefix="phones-filter"
            filter={filter}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            onFilterChange={onFilterChange}
            activeOptions={activeOptions}
            isDisabled={false}
            dataLayerEvent="phonesFilter"
          />
        );
      })}

      <MobileContainer>
        {/* Mobile Filters */}
        <IconPillButton
          ref={phonesFilterRef}
          aria-haspopup="true"
          aria-expanded={bladeVisible}
          className="btn-allFilters"
          active={filtersActivated}
          icon="filter"
          onClick={() => {
            setBladeVisible(true);
            dataLayerFilterSubmit(
              'phonesFilter',
              'All filters',
              'Open',
            );
          }}
          disabled={phonesCount === 0}
          dataTestId="phone-all-filters"
          iconAlignment="left"
        >
          {phoneFiltersLabel}
        </IconPillButton>
        <FilterDrawer
          drawerState={bladeVisible}
          setDrawerState={setBladeVisible}
          drawerLabel={bladeTitle}
          closeLabel={bladeCloseLabel}
          itemsCount={phonesCount.toString()}
          onClearFilters={onClearFilters}
          resultCta={seePhonesLabel}
          clearCta={clearFiltersLabel}
          disableClearAll={!filtersActivated}
          filtersActivated={filtersActivated}
          noResultCta={phoneNotFoundLabel}
          scrollToFilters={scrollToFilters}
          dataLayerEvent="phonesFilter"
        >
          {/* Content inside blade */}
          <FilterWrapper>
            {filteredFilters.map(
              (eachFilter: Record<string, any>, index: number) => eachFilter.id !== 'phone'
            && eachFilter.id !== 'sort'
            && eachFilter.id !== 'colour' && (
            <FilterGroup
              key={eachFilter.name}
              filter={eachFilter}
              active={eachFilter.options.length > 1}
              onFilterChange={onFilterChange}
              uniqueId={`${index}${tokenize(eachFilter.name)}`}
              dataLayerEvent="phonesFilter"
            />
              ),
            )}
          </FilterWrapper>
        </FilterDrawer>

        <TextLink
          linkText={clearFiltersLabel?.ctaLabel?.ctaLabel}
          linkColor="black"
          linkSize="small"
          route="function"
          onClick={onClearFilters}
          data-testid="phones-clear-all"
          disabled={!filtersActivated}
        />
      </MobileContainer>

    </FiltersContainer>
    
  );
};

export default PhoneFilters;
